/**
 * イベントの作り方（5通り）の定義 — SSOT
 *
 * ダッシュボードの作成ボタン長押しで開くスピードダイヤルと、
 * ヘッダーメニューの作成セクションで同じ並び・アイコン・遷移先を使う。
 *
 * `state` は遷移先の作成ページ（`CreateEvent`）が読むモーダル自動オープンフラグ。
 * 省略した項目は何も開かず、空の作成フォームをそのまま表示する。
 */
import type { ComponentType, SVGProps } from 'react';
import {
  SparklesIcon,
  LinkIcon,
  RectangleStackIcon,
  LightBulbIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';

export interface CreateEventAction {
  /** React の key と、テストから参照するための安定した識別子 */
  key: string;
  /** スピードダイヤルのラベル。「〜から」まで含む */
  label: string;
  /** 幅が限られる場所（メニューのアイコングリッド）で使う短い名前 */
  shortLabel: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** CreateEvent が読むモーダル自動オープンフラグ（省略時は通常の作成フォーム） */
  state?: Record<string, boolean>;
  /** アイコン円の背景色（ブランドパレット）。スピードダイヤルとヘッダーメニュー用 */
  iconBg: string;
  /** 塗りつぶさずアイコンだけ色を付けるとき用（かんたん作成パネル） */
  iconColor: string;
  /** インフォチップで出す一行の説明 */
  description: string;
}

export const CREATE_EVENT_ACTIONS: CreateEventAction[] = [
  {
    key: 'ai',
    label: 'AIから',
    shortLabel: 'AI',
    icon: SparklesIcon,
    state: { openAIModal: true },
    iconBg: 'bg-primary-600',
    iconColor: 'text-primary-600',
    description:
      '3つの質問に答えると、AIがイベント企画を提案します',
  },
  {
    key: 'url',
    label: 'URLから',
    shortLabel: 'URL',
    icon: LinkIcon,
    state: { openImportModal: true },
    iconBg: 'bg-secondary-600',
    iconColor: 'text-secondary-600',
    description:
      '告知ページのURLを貼ると、イベント名・日時・場所を読み取ります',
  },
  {
    key: 'template',
    label: 'テンプレートから',
    shortLabel: 'ひな形',
    icon: RectangleStackIcon,
    state: { openTemplateGallery: true },
    iconBg: 'bg-accent-600',
    iconColor: 'text-accent-600',
    description:
      '忘年会・歓送迎会・お花見など、定番のひな形から選びます',
  },
  {
    key: 'idea',
    label: 'アイデア帳から',
    shortLabel: 'アイデア',
    icon: LightBulbIcon,
    state: { openIdeaPicker: true },
    iconBg: 'bg-warning-500',
    iconColor: 'text-warning-600',
    description:
      '条件で絞り込めるイベントアイデア集から選びます',
  },
  // スピードダイヤル表示中は FAB が閉じるボタンに変わるため、通常の作成もここに出す
  {
    key: 'form',
    label: 'フォームから',
    shortLabel: 'フォーム',
    icon: PencilSquareIcon,
    iconBg: 'bg-text-600',
    iconColor: 'text-text-600',
    description:
      '何も入っていない作成フォームをそのまま開きます',
  },
];
